import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { calculateDropdownPosition, closeAllDropdowns, CLOSE_ALL_DROPDOWNS_EVENT, FLOWCHART_PAN_START_EVENT } from '../shared/utils/dropdownPositioning';

/**
 * Eye icon for the dropdown button
 */
const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * Visibility items configuration
 */
const VISIBILITY_ITEMS = [
  { id: 'activeMissions', label: 'Active Missions', description: 'Tasks currently in progress' },
  { id: 'availableMissions', label: 'Available Missions', description: 'Tasks ready to start' },
  { id: 'milestones', label: 'Milestones', description: 'Campaign checkpoints with progress' },
  { id: 'minimap', label: 'Minimap', description: 'Navigation overview' },
  { id: 'campaignsKey', label: 'Campaigns Key', description: 'Multi-campaign legend' },
];

/**
 * VisibilityDropdown Component
 * 
 * Dropdown to toggle visibility of HUD elements in Flow view.
 * Settings are stored in user preferences (DynamoDB).
 */
const VisibilityDropdown = ({ 
  visibility = {}, 
  onVisibilityChange,
  isMultiCampaignMode = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter items based on mode (campaigns key only shows in multi-campaign mode)
  const visibleItems = VISIBILITY_ITEMS.filter(item => {
    if (item.id === 'campaignsKey') return isMultiCampaignMode;
    return true;
  });

  // Calculate dropdown position with edge detection
  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition(calculateDropdownPosition(rect, 240));
    }
  };

  // Open dropdown
  const handleToggle = () => {
    if (!isOpen) {
      closeAllDropdowns(buttonRef.current); // Close any other open dropdowns first
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  // Listen for global close events (when another dropdown opens or view zooms/pans)
  useEffect(() => {
    const handleCloseAll = () => setIsOpen(false);
    window.addEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
    window.addEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    return () => {
      window.removeEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
      window.removeEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    };
  }, []);

  // Toggle a visibility item
  const handleItemToggle = (itemId) => {
    const newVisibility = {
      ...visibility,
      [itemId]: !visibility[itemId],
    };
    onVisibilityChange?.(newVisibility);
  };

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Count visible items
  const visibleCount = visibleItems.filter(item => visibility[item.id] !== false).length;
  const totalCount = visibleItems.length;

  return (
    <>
      <button
        ref={buttonRef}
        className={`toolbar-btn ${isOpen ? 'toolbar-btn--active' : ''}`}
        onClick={handleToggle}
        title="Toggle HUD element visibility"
      >
        <span className="toolbar-btn__spacer" />
        <span className="toolbar-btn__content">
          <span className="toolbar-btn__icon">
            <EyeIcon />
          </span>
          <span className="toolbar-btn__label">Customize HUD</span>
        </span>
        <span className="toolbar-btn__chevron">
          <ChevronIcon />
        </span>
      </button>

      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="visibility-dropdown"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            maxHeight: dropdownPosition.maxHeight,
            overflowY: 'auto',
            zIndex: 50000,
          }}
        >
          <div className="visibility-dropdown__header">
            <span>Show/Hide Elements</span>
            <span className="visibility-dropdown__count">
              {visibleCount}/{totalCount}
            </span>
          </div>

          <div className="visibility-dropdown__body">
            {visibleItems.map(item => {
              const isEnabled = visibility[item.id] !== false; // Default to true
              
              return (
                <button
                  key={item.id}
                  className={`visibility-dropdown__item ${isEnabled ? 'visibility-dropdown__item--enabled' : ''}`}
                  onClick={() => handleItemToggle(item.id)}
                  title={item.description}
                >
                  <span className="visibility-dropdown__checkbox">
                    {isEnabled && <CheckIcon />}
                  </span>
                  <span className="visibility-dropdown__label">
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default VisibilityDropdown;
